import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { cn } from "@nt/ui/cn";
import { radioRecipe } from "@nt/ui/recipes";
import type { ComponentType } from "react";

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
}

const COLUMNS = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-3",
} as const;

/**
 * A single choice as bordered cards, one per option, on Base UI's RadioGroup:
 * the whole card is the radio, arrow keys move between cards, and the chosen
 * card wears the forest edge and tint. Replaces native radio buttons.
 */
export function ChoiceCards<T extends string>({
  name,
  value,
  onChange,
  options,
  columns = 1,
  disabled = false,
  className,
  "aria-label": ariaLabel,
}: {
  name: string;
  value: T | null;
  onChange: (value: T) => void;
  options: readonly ChoiceOption<T>[];
  columns?: 1 | 2 | 3;
  disabled?: boolean;
  className?: string;
  "aria-label": string;
}) {
  return (
    <RadioGroup
      data-slot="choice-cards"
      name={name}
      aria-label={ariaLabel}
      value={value ?? ""}
      disabled={disabled}
      onValueChange={(next) => onChange(next as T)}
      className={cn("grid gap-3", COLUMNS[columns], className)}
    >
      {options.map((o) => {
        const descriptionId = o.description
          ? `${name}-${o.value}-description`
          : undefined;
        return (
          <Radio.Root
            key={o.value}
            value={o.value}
            data-slot="choice-card"
            aria-describedby={descriptionId}
            className={cn(
              "group/choice relative flex w-full items-start gap-3 rounded-card border border-border bg-card p-4 text-left outline-none transition-[border-color,box-shadow,background-color] duration-150",
              "hover:border-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              "data-checked:border-primary data-checked:bg-primary/5 data-checked:ring-1 data-checked:ring-primary",
              "data-disabled:cursor-not-allowed data-disabled:opacity-50",
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                radioRecipe.root,
                "mt-0.5 group-data-checked/choice:border-primary group-data-checked/choice:bg-primary",
              )}
            >
              <Radio.Indicator className={radioRecipe.indicator}>
                <span className={radioRecipe.dot} />
              </Radio.Indicator>
            </span>
            {o.icon ? (
              <o.icon
                aria-hidden
                className="mt-0.5 size-5 shrink-0 text-muted-foreground group-data-checked/choice:text-primary"
              />
            ) : null}
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm font-medium text-foreground">
                {o.label}
              </span>
              {o.description ? (
                <span
                  id={descriptionId}
                  className="text-compact text-muted-foreground"
                >
                  {o.description}
                </span>
              ) : null}
            </span>
          </Radio.Root>
        );
      })}
    </RadioGroup>
  );
}
